import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
  Avatar,
  useMediaQuery,
  Badge,
  Tooltip,
  Chip,
} from '@mui/material';
import { Menu as MenuIcon, ExitToApp, Email, People, CheckCircle, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TopNav = ({ toggleSidebar }) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const [hospitalData, setHospitalData] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [staffCounts, setStaffCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    const dummyHospital = {
      hospital_name: 'Human Resource Management',
    };
    setHospitalData(dummyHospital);
  }, []);

  useEffect(() => {
    const fetchUserName = async () => {
      const email = localStorage.getItem('email');
      if (!email) return;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/get_adminuser_names.php`,
          { email },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (response.data.success && response.data.data) {
          setFirstName(response.data.data.first_name);
        } else {
          console.error('User fetch failed:', response.data.message);
        }
      } catch (err) {
        console.error('API error:', err);
      }
    };

    fetchUserName();
  }, []);

  useEffect(() => {
    const fetchStaffCounts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/Staff_Count.php`
        );
        if (response.data.success) {
          setStaffCounts(response.data.data);
        } else {
          console.error('Failed to fetch staff counts:', response.data.message);
        }
      } catch (err) {
        console.error('API error (staff counts):', err);
      }
    };

    fetchStaffCounts();
  }, []);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('email');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('phoneNumber');
    sessionStorage.clear();
    navigate('/');
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <AppBar
      position="sticky"
      elevation={3}
      sx={{
        top: 0,
        backgroundColor: '#C84646', // Oxblood red
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '48px' }}>
        {isMobile && (
          <IconButton color="inherit" onClick={toggleSidebar}>
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              {hospitalData?.hospital_name}
            </Typography>
            
            {/* Staff Count Display with Active/Inactive Breakdown */}
            {staffCounts.total > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title="Total Staff">
                  <Chip
                    icon={<People sx={{ color: 'white' }} />}
                    label={`Total: ${staffCounts.total}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                </Tooltip>
                
                <Tooltip title="Active Staff">
                  <Chip
                    icon={<CheckCircle sx={{ color: '#4caf50' }} />}
                    label={`Active: ${staffCounts.active}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(76, 175, 80, 0.2)', 
                      color: 'white',
                      '& .MuiChip-icon': { color: '#4caf50' }
                    }}
                  />
                </Tooltip>
                
                <Tooltip title="Inactive Staff">
                  <Chip
                    icon={<Cancel sx={{ color: '#f44336' }} />}
                    label={`Inactive: ${staffCounts.inactive}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(244, 67, 54, 0.2)', 
                      color: 'white',
                      '& .MuiChip-icon': { color: '#f44336' }
                    }}
                  />
                </Tooltip>
              </Box>
            )}

            {/* Email Button */}
            <Tooltip title="Webmail">
              <IconButton
                color="inherit"
                onClick={() => window.open('https://myelegante.com/webmail', '_blank', 'noopener,noreferrer')}
              >
                <Email />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="h6" sx={{ color: 'white' }}>
            {getGreeting()}, {firstName || 'User'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {firstName ? firstName.charAt(0).toUpperCase() : 'U'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            MenuListProps={{ 'aria-labelledby': 'profile-button' }}
          >
            <MenuItem onClick={() => handleNavigate('#')}>Change Password</MenuItem>
          </Menu>

          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout}>
              <ExitToApp />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;